import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  BarChart3, Route, MapPin, Clock, CheckCircle, XCircle, 
  SkipForward, TrendingUp, TrendingDown, Loader2, Target
} from 'lucide-react';

export default function AnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadLifetimeStats();
  }, []);

  const loadLifetimeStats = async () => {
    try {
      const res = await api.getLifetimeAnalytics();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load lifetime stats:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '-';
    return num.toLocaleString();
  };

  const formatMiles = (miles) => {
    if (!miles && miles !== 0) return '-';
    return `${miles.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi`;
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatVariance = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const sign = minutes > 0 ? '+' : '';
    return `${sign}${formatDuration(Math.abs(minutes))}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-teal-500" />
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Lifetime Analytics</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Cumulative stats across all your routes
          </p>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Routes Completed */}
        <Card className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border-teal-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Route className="h-4 w-4 text-teal-500" />
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Routes</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {formatNumber(stats?.total_routes_completed)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Completed</p>
          </CardContent>
        </Card>

        {/* Stops Served */}
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Served</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {formatNumber(stats?.total_stops_served)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Successful stops</p>
          </CardContent>
        </Card>

        {/* Success Rate */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Success</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats?.success_rate || 0}%
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Service rate</p>
          </CardContent>
        </Card>

        {/* Distance - Show actual if available, estimated otherwise */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Distance</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {formatMiles(stats?.total_actual_miles || stats?.total_estimated_miles)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {stats?.total_actual_miles ? 'Actual miles' : 'Estimated miles'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Stop Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              Stop Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                  <span className="text-sm text-slate-600 dark:text-slate-300">Total Attempted</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatNumber(stats?.total_stops_attempted)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-slate-600 dark:text-slate-300">Served</span>
                </div>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatNumber(stats?.total_stops_served)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-sm text-slate-600 dark:text-slate-300">Unsuccessful</span>
                </div>
                <span className="font-semibold text-red-600 dark:text-red-400">
                  {formatNumber(stats?.total_stops_unsuccessful)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-slate-600 dark:text-slate-300">Skipped</span>
                </div>
                <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                  {formatNumber(stats?.total_stops_skipped)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              Time Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-300">Total Time on Routes</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatDuration(stats?.total_estimated_duration_minutes)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-300">Average Variance</span>
                <span className={`font-semibold ${
                  stats?.average_variance_minutes < 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : stats?.average_variance_minutes > 0 
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-slate-500'
                }`}>
                  {formatVariance(stats?.average_variance_minutes)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Distance Stats - Only show comparison when we have BOTH estimated AND actual data */}
        {stats?.total_actual_miles > 0 && stats?.total_estimated_miles > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                Mileage Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Estimated Miles</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {formatMiles(stats?.total_estimated_miles)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Actual Miles (GPS)</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {formatMiles(stats?.total_actual_miles)}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-slate-200 dark:border-slate-700 pt-2">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Difference</span>
                  <span className={`font-semibold ${
                    (stats?.total_actual_miles - stats?.total_estimated_miles) > 0 
                      ? 'text-red-600 dark:text-red-400' 
                      : (stats?.total_actual_miles - stats?.total_estimated_miles) < 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-slate-500'
                  }`}>
                    {((stats?.total_actual_miles - stats?.total_estimated_miles) > 0 ? '+' : '')}
                    {formatMiles(stats?.total_actual_miles - stats?.total_estimated_miles)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Empty State / Tip */}
      {stats?.total_routes_completed === 0 && (
        <Card className="bg-slate-50 dark:bg-slate-800/50 border-dashed">
          <CardContent className="p-6 text-center">
            <Route className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <h3 className="font-medium text-slate-900 dark:text-white mb-1">No routes completed yet</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Complete your first route to start tracking your lifetime analytics.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
